/**
 * `_requestId` is the value the portal injects into a dataset request to form part of
 * the DataHub cache key (see DataHubController: it is merged into `filtersState`, which
 * changes the params hash the job caches under, and is also passed as the portal's own
 * cache salt). Whatever this returns therefore decides which cached response a viewer
 * sees.
 *
 * It used to be a random UUID persisted forever, which made it a cache *pin* rather
 * than a cache buster: once a viewer pressed Refresh, their key never changed again and
 * the only thing that ever renewed their data was the job's TTL expiring — up to 60
 * minutes for a `rows` step, 240 for `countAll`.
 *
 * The default is now a shared time bucket. Every viewer inside the same window computes
 * the identical token, so they still share one cached response and the expensive payloads
 * are paid once globally rather than per person; when the window rolls over the token
 * changes and the next request repopulates the cache. The bucket length is therefore the
 * maximum age of data a screen can show, independently of the TTLs, which only have to
 * outlive one bucket for the entry to survive its own window.
 *
 * The bucket is evaluated when a screen mounts, not on a timer: the guarantee is "at most
 * one bucket old when you open the screen". Continuously updating an open screen would be
 * auto-refresh, which is a different feature.
 */

/** Maximum age of data a screen can show without the viewer pressing Refresh. */
export const CACHE_BUCKET_MS = 10 * 60 * 1000;

/** Deterministic from the clock alone, so all viewers in the window agree. */
export const currentBucketToken = (now: number = Date.now()): string =>
  `b${Math.floor(now / CACHE_BUCKET_MS)}`;

interface StoredRequestId {
  id: string;
  at: number;
}

const parse = (raw: string | null): StoredRequestId | null => {
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw);
    if (
      parsed &&
      typeof parsed.id === "string" &&
      typeof parsed.at === "number"
    ) {
      return parsed as StoredRequestId;
    }
  } catch {
    // Not JSON. Either a legacy bare-UUID token from before this change or corrupted
    // storage — both are treated as absent, which is what un-pins the viewers who
    // still carry one.
  }
  return null;
};

/**
 * A manual Refresh wins over the shared bucket, but only for one bucket length. Longer
 * would recreate the pin; shorter would lose the token across the hard reload that
 * Ctrl+F5 performs, which is the only reason it is persisted at all.
 */
export const readRequestId = (
  storageKey: string,
  now: number = Date.now(),
): string => {
  let stored: StoredRequestId | null = null;
  try {
    stored = parse(localStorage.getItem(storageKey));
  } catch {
    // localStorage can throw in a locked-down browser context; fall back to the bucket.
  }
  if (stored && now - stored.at < CACHE_BUCKET_MS) return stored.id;
  return currentBucketToken(now);
};

/** Records a manual Refresh so it survives the reload Ctrl+F5 triggers. */
export const writeManualRequestId = (
  storageKey: string,
  id: string,
  now: number = Date.now(),
): void => {
  try {
    localStorage.setItem(storageKey, JSON.stringify({ id, at: now }));
  } catch {
    // Storage unavailable — the in-memory state still applies for this mount.
  }
};
